/**
 * POM-6223 — what each typed backend error code MEANS for the row the operator just acted on.
 *
 * KEY ON THE CODE, NEVER THE MESSAGE. Getting this wrong is how an operator double-dispenses:
 * three of these codes are raised on a path where the prescription HAS ALREADY BEEN RELEASED and
 * an order exists, and a second click on Bring Back publishes a second message under a different
 * deduplication id — two orders, two dispenses, no undo.
 *
 * POM-6279 adds the action-policy refusal, the synchronous ESP / POM Retry codes and the relay-fax
 * codes (RELAY_FAX_*). Same rules: key on the code, and an unrecognised code is pessimistic.
 *
 * Each entry declares:
 *   severity  — MUI Alert / notistack severity.
 *   outcome   — what actually happened to the row. See OUTCOME below.
 *   retryable — whether the confirm button stays on screen. An EXPLICIT per-code flag, absent
 *               (falsy) on all but one code; see nonRoutableRetryIsSafe for why it is not derived
 *               from `outcome`.
 *   title     — the one-line headline.
 *   detail    — what the operator does next, in plain words.
 *
 * The wording tracks the docblocks in smartphos-core `enum/errorEnum.ts`; if those change, this
 * changes with them.
 */

export const OUTCOME = {
  /** Nothing was changed. A refresh, a re-read and another attempt are all safe. */
  NOTHING_HAPPENED: 'NOTHING_HAPPENED',
  /** The statement failed mid-flight. The row's state is UNKNOWN. A retry is NOT safe. */
  UNKNOWN: 'UNKNOWN',
  /**
   * The row moved into a state NO operator action can move it out of, but nothing clinical
   * happened — nothing was published, so no order exists.
   *
   * Deliberately NOT `UNKNOWN`: the state is known exactly, and telling an operator it is unknown
   * would invite them to fear a dispense that provably did not occur. Deliberately NOT
   * `NOTHING_HAPPENED` either: something DID change — the row is no longer Pending and both
   * mutations now refuse it — so "refresh and decide again" would be false advice.
   */
  STUCK: 'STUCK',
  /** The irreversible thing HAPPENED. A second click would duplicate it. */
  DONE: 'DONE',
};

// Raised by the reads (the list and the by-id lookup).
const READ_CODES = {
  NON_ROUTABLE_REQUEST_READ_FAILED: {
    severity: 'error',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'This queue did not load',
    detail:
      'The list could not be read, so this screen is showing nothing — that is NOT the same as ' +
      'nothing being parked. Try again; if it keeps failing, escalate to engineering.',
  },
  NON_ROUTABLE_REQUEST_FILTER_INVALID: {
    severity: 'error',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'That filter could not be applied',
    detail:
      'No rows were loaded. This list is not empty — it did not run. Correct or clear the ' +
      'filters and try again.',
  },
  NON_ROUTABLE_REQUEST_ID_INVALID: {
    severity: 'error',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'That request id is not valid',
    detail: 'Nothing was changed. Refresh the queue and act on a row from the list.',
  },
  NON_ROUTABLE_REQUEST_NOT_FOUND: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'No parked request carries that id',
    detail: 'Nothing was changed. Refresh the queue — the row may have been removed since.',
  },
};

// Raised by the resolution mutations. MOST of these codes are shared by more than one of
// them, so the wording is kept action-neutral: a code raised by Retry must not tell an operator
// that somebody is RELEASING the request, because "releasing" means an order is being created and
// that is a materially more alarming claim than the one the backend actually made. Where a code
// genuinely belongs to one action only — CANCEL_LOST, and the RETRY_* block lower down — the copy
// names that action deliberately.
const ACTION_CODES = {
  NON_ROUTABLE_REQUEST_NOT_PENDING: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'Somebody already acted on this request',
    detail:
      'It is no longer Pending, so nothing was changed by this click. Refresh the queue and ' +
      're-check what happened to it before acting again — it may have been released, cancelled, ' +
      're-routed by a retry, or be mid-resolution under somebody else. Those are not the same ' +
      'outcome, and some of them filled the prescription.',
  },
  // Raised by more than one action, so the title must not name any outcome. It used to say
  // "Another admin is releasing this request", which told a Retry operator an order was being
  // created when the backend had only reported losing a claim race.
  NON_ROUTABLE_REQUEST_CLAIM_LOST: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'Another admin claimed this request first',
    detail:
      'They won the race, so NOTHING was changed here. Refresh the queue to see what they did — ' +
      'do not assume your click did anything.',
  },
  // Only reachable if the backend's reason list and this portal's drift apart during a deploy.
  NON_ROUTABLE_REQUEST_CANCEL_REASON_INVALID: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'That cancel reason is not accepted',
    detail: 'Nothing was changed. Close this dialog, re-open it and choose a different reason.',
  },
  NON_ROUTABLE_REQUEST_CANCEL_LOST: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'Another admin moved this request first',
    detail:
      'It changed between your last refresh and this click, so NOTHING was changed here. ' +
      'Refresh the queue and decide again.',
  },
  // Also raised by Retry, so "cannot be released" would be the wrong noun for half its callers.
  NON_ROUTABLE_REQUEST_MESSAGE_UNUSABLE: {
    severity: 'error',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'This request cannot be acted on',
    detail:
      'Its stored request cannot be replayed or published. Nothing was changed and the request ' +
      'is still Pending. Escalate to engineering — trying again will not help.',
  },
  // ONE OF THE THREE codes that earn a second click (see the retryable test, which names all
  // three), and only because of a condition the backend now
  // CHECKS rather than assumes: it is raised solely when the rollback to PENDING reported
  // `affected === 1`. When the rollback does not land the backend raises RELEASE_ROLLBACK_FAILED
  // instead. An earlier revision threw this one unconditionally, so an operator could be invited
  // to retry a request that was in fact stuck and that every mutation would have refused.
  NON_ROUTABLE_REQUEST_RELEASE_PUBLISH_FAILED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    retryable: true,
    title: 'Nothing was released',
    detail:
      'The publish failed and the request has been put back to Pending, so no order was ' +
      'created and the request really is parked again. You can try again.',
  },
  // The sibling of the code above, and the whole reason it was split in two.
  NON_ROUTABLE_REQUEST_RELEASE_ROLLBACK_FAILED: {
    severity: 'error',
    outcome: OUTCOME.STUCK,
    title: 'This request is STUCK and needs manual review',
    detail:
      'Nothing was sent, so no order was created and no prescription is being dispensed. But the ' +
      'request could not be returned to Pending either, so it is now sitting in Resolution In ' +
      'Progress — where NO action on this screen will clear it. Do not try again; escalate to ' +
      'engineering so the record can be freed.',
  },
  // ---- RETRY ROUTING ----------------------------------------------------------------------
  // The backend re-invokes the Routing Hub rather than replaying the decision stored at intake,
  // and FAILS CLOSED: when the hub cannot be reached, or answers with the order un-enriched, the
  // request is left parked rather than replayed on a stale decision. That is this code, and it is
  // the ONLY retry outcome that earns a second click — nothing was sent and the row really is back
  // at Pending, so the operator can fix the configuration and try again.
  NON_ROUTABLE_REQUEST_ROUTING_UNAVAILABLE: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    retryable: true,
    title: 'Routing could not be re-run',
    detail:
      'The routing service produced no decision, so nothing was replayed and no order was ' +
      'created. The request is still Pending. Try again once the routing configuration is in ' +
      'place.',
  },
  // The transport failure, after a rollback that DID land. Same shape as the release split above.
  NON_ROUTABLE_REQUEST_RETRY_PUBLISH_FAILED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    retryable: true,
    title: 'Nothing was replayed',
    detail:
      'The replay could not be sent and the request has been put back to Pending, so no order ' +
      'was created. You can try again.',
  },
  // The sibling where the rollback did NOT land, and the reason the pair is split.
  NON_ROUTABLE_REQUEST_RETRY_ROLLBACK_FAILED: {
    severity: 'error',
    outcome: OUTCOME.STUCK,
    title: 'This request is STUCK and needs manual review',
    detail:
      'Nothing was replayed, so no order was created and no prescription is being dispensed. But ' +
      'the request could not be returned to Pending either, so it is now sitting in Resolution ' +
      'In Progress. Do not try again now; escalate to engineering so the record can be freed. ' +
      '(On an ESP / POM order, Retry comes back on this row after 15 minutes.)',
  },
  // A CONFIGURATION fault on the routing queue specifically. Fixed by infrastructure, not by
  // clicking again, so it is not retryable however harmless the request's state is.
  NON_ROUTABLE_REQUEST_RETRY_QUEUE_UNAVAILABLE: {
    severity: 'error',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'The routing queue is not configured for this stack',
    detail:
      'Nothing was replayed and the request is still parked. This needs an infrastructure fix ' +
      'rather than another attempt — escalate to engineering.',
  },
  // THE REPLAY ALREADY HAPPENED. Read this one carefully before deciding what to tell an operator:
  // the routing workflow is running right now and may already have produced an order. Only the
  // read-back of the row failed.
  NON_ROUTABLE_REQUEST_RETRY_READ_BACK_FAILED: {
    severity: 'warning',
    // DONE, not NOTHING_HAPPENED: the replay was sent, so a second click would replay it again.
    outcome: OUTCOME.DONE,
    title: 'The retry was sent, but the result could not be read back',
    detail:
      'Routing has run, or is running, again for this request and may already have created an ' +
      'order. Do not try again. Refresh the queue in a moment to see where the request ended up.',
  },
  // A CONFIGURATION fault, not a transport one, and the two are fixed by different people. The
  // backend raises this only after the rollback landed, so the request really is parked again —
  // but retrying will fail identically until the stack is fixed, so it is not retryable.
  NON_ROUTABLE_REQUEST_QUEUE_UNAVAILABLE: {
    severity: 'error',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'The onboarding queue is not configured for this stack',
    detail:
      'There was nowhere to send the request, so nothing was sent, no order was created, and the ' +
      'request is still parked and Pending. This is an infrastructure fault rather than a ' +
      'transient one — clicking again will fail the same way. Escalate to engineering.',
  },
  NON_ROUTABLE_REQUEST_CLAIM_FAILED: {
    severity: 'error',
    outcome: OUTCOME.UNKNOWN,
    title: 'The database did not answer — the state of this request is UNKNOWN',
    detail:
      'It may or may not have been claimed. DO NOT try again. Refresh the queue, and if the ' +
      'request is sitting in Resolution In Progress, escalate to engineering — a human has to ' +
      'look at the record.',
  },
  NON_ROUTABLE_REQUEST_CANCEL_FAILED: {
    severity: 'error',
    outcome: OUTCOME.UNKNOWN,
    title: 'The database did not answer — the state of this request is UNKNOWN',
    detail:
      'The cancel may already have committed. DO NOT try again. Refresh the queue and escalate ' +
      'to engineering — a human has to look at the record.',
  },
  NON_ROUTABLE_REQUEST_RESOLUTION_NOT_RECORDED: {
    severity: 'error',
    outcome: OUTCOME.DONE,
    // Raised by Bring Back, Move to POM and an ESP / POM Retry whose close failed, so the copy
    // names and forbids all three.
    title: 'THE REQUEST WAS RELEASED OR ROUTED — an order now exists and will be dispensed',
    detail:
      'Only the bookkeeping write failed, so the record still reads Resolution In Progress. ' +
      'DO NOT click Bring Back, Retry or Move to POM again: that would dispense this ' +
      'prescription a second time, and there is no undo. Escalate to engineering so the record ' +
      'can be corrected.',
  },
  NON_ROUTABLE_REQUEST_RELEASED_READ_BACK_FAILED: {
    severity: 'success',
    outcome: OUTCOME.DONE,
    title: 'Released to POM — an order now exists and will be dispensed',
    detail:
      'Everything succeeded; only the read that renders the result did not answer. Re-open the ' +
      'record to see it. Do not click Bring Back or Move to POM again.',
  },
  NON_ROUTABLE_REQUEST_CANCELLED_READ_BACK_FAILED: {
    severity: 'success',
    outcome: OUTCOME.DONE,
    title: 'Cancelled — this prescription will never be filled',
    detail:
      'Everything succeeded; only the read that renders the result did not answer. Re-open the ' +
      'record to see it. Do not click Cancel again.',
  },
};

// Raised by any action (POM-6279): the server's action policy refuses an action this row does not
// offer — the same table `availableActions` is built from, so this only fires when the row changed
// between the menu opening and the click.
const POLICY_CODES = {
  NON_ROUTABLE_REQUEST_ACTION_NOT_ALLOWED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'That action is not available on this request',
    detail:
      'Nothing was changed. The actions a request offers depend on how it was parked and what ' +
      'has happened to it since. Refresh the queue and use what the row menu offers now.',
  },
};

// ---- ESP / POM RETRY (POM-6279 P4) -----------------------------------------------------------
// An ESP_POM row is retried SYNCHRONOUSLY: the order is re-run inside the mutation and the replay
// closes or re-parks the row itself. These are what a Retry that did not end cleanly reports, and
// several are raised AFTER an order may already exist — the same double-dispense hazard as the
// release codes above. Wording tracks the docblocks in errorEnum.ts and the messages in
// nonRoutableEspPomRetryService.ts.
const ESP_POM_RETRY_CODES = {
  // The replay failed (a throw or a rejected order) and the claim WAS returned to Pending with
  // last_error RETRY:<code>. Retryable by the backend's own contract: a next Retry resumes a
  // half-written relay idempotently, or reports RETRY_POM_ORDER_EXISTS — it cannot create a second
  // order. That is the one property every retryable code here shares.
  NON_ROUTABLE_REQUEST_RETRY_REPLAY_FAILED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    retryable: true,
    title: 'The retry failed and the request is parked again',
    // NOT "the order was not routed": the replay may have written relay groups, or a whole
    // order's POM half, before it failed.
    detail:
      'Part of the order may already have been relayed or created — check the Relay tab before ' +
      'cancelling. A retry resumes safely.',
  },
  NON_ROUTABLE_REQUEST_RETRY_ROUTED_WITH_ERRORS: {
    severity: 'warning',
    outcome: OUTCOME.DONE,
    title: 'Routed — but a later step failed',
    detail:
      'The order WAS routed and this request is closed. A step after routing (such as labels) ' +
      'failed, so check the order itself. Do not retry this request.',
  },
  NON_ROUTABLE_REQUEST_RETRY_REPARKED_WITH_ERRORS: {
    severity: 'warning',
    outcome: OUTCOME.DONE,
    title: 'Parked again — but a later step failed',
    detail:
      'Routing ran and the request is parked again. If part of the order was routed, an order ' +
      'WAS created for those lines and a later step on it failed — check that order before doing ' +
      'anything else here.',
  },
  NON_ROUTABLE_REQUEST_RETRY_OUTCOME_UNKNOWN: {
    severity: 'error',
    outcome: OUTCOME.UNKNOWN,
    title: 'The retry did not finish — part of the order MAY have been created',
    detail:
      'Do not try again now. The request is held for review. After 15 minutes Retry comes back on ' +
      'this row and will find out what exists; escalate to engineering if you are unsure.',
  },
  // Nothing happened on THIS click and the row is Pending — but an order already holds the
  // reference, most likely from an earlier interrupted retry, so retrying cannot help.
  NON_ROUTABLE_REQUEST_RETRY_POM_ORDER_EXISTS: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'An order already exists for this reference',
    detail:
      'Nothing was re-run. An earlier retry most likely created that order. Its routing-rule lines ' +
      'need to be handled by hand; once they are, cancel this request.',
  },
  NON_ROUTABLE_REQUEST_RETRY_RULE_LINES_UNPARKED: {
    severity: 'error',
    outcome: OUTCOME.DONE,
    title: 'An order WAS created, but some lines are held nowhere',
    // Retry is NOT forbidden here — after 15 minutes it is the row's only exit: the stale-claim
    // take-over reports the existing order and returns the request to Pending, where it can be
    // cancelled (nonRoutableEspPomRetryService failRuleLinesUnparked / STALE_ESCAPE).
    detail:
      'The order was created but its rule lines were not parked. Handle the rule lines; after 15 ' +
      'minutes, Retry returns this request to the queue so it can be cancelled.',
  },
  // A park or re-park that could not be WRITTEN. A Retry reports it through the codes above; this
  // copy exists so the code never falls through to "not recognised" if it ever does surface.
  NON_ROUTABLE_REQUEST_PARK_FAILED: {
    severity: 'error',
    outcome: OUTCOME.UNKNOWN,
    title: 'The request could not be parked — its state is UNKNOWN',
    detail: 'Do not try again. Refresh the queue and escalate to engineering.',
  },
};

// ---- RELAY FAX (POM-6279) --------------------------------------------------------------------
// Resend / Reconcile / Cancel fax. A FAX CANNOT BE RECALLED, which is what most of these turn on.
const RELAY_FAX_CODES = {
  // The Resend dialog turns this into its phone-confirmation step rather than showing it as an
  // error. The copy is here so any other surface still says what happened.
  RELAY_FAX_RESEND_CONFIRMATION_REQUIRED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'Phone the pharmacy before resending',
    detail:
      'Nothing was resent. We cannot tell whether the last fax reached the pharmacy. Resend only ' +
      'after the pharmacy confirms by phone that it did NOT receive it.',
  },
  RELAY_FAX_ORDER_CHANGED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'This fax changed before your action could run',
    detail:
      'Nothing was changed. It may have been delivered, resent or cancelled in the meantime. ' +
      'Refresh the queue and decide again.',
  },
  // The only retryable fax code: the backend rolled the resend back and the row is Pending again.
  RELAY_FAX_RESEND_PUBLISH_FAILED: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    retryable: true,
    title: 'Nothing was resent',
    detail:
      'The resend could not be queued and was rolled back, so the fax is parked exactly as ' +
      'before. You can try again.',
  },
  RELAY_FAX_RESEND_ROLLBACK_FAILED: {
    severity: 'error',
    outcome: OUTCOME.STUCK,
    title: 'This fax is STUCK and needs manual review',
    detail:
      'The resend could not be queued, and the request could not be put back either. Do not try ' +
      'again; escalate to engineering.',
  },
  RELAY_FAX_ACTION_FAILED: {
    severity: 'error',
    outcome: OUTCOME.UNKNOWN,
    title: 'The result of this action is UNKNOWN',
    detail:
      'The database did not confirm it. Do not try again. Refresh the queue and escalate to ' +
      'engineering — someone has to check the request and its fax order.',
  },
  RELAY_FAX_ACTION_READ_BACK_FAILED: {
    severity: 'success',
    outcome: OUTCOME.DONE,
    title: 'Done — only the confirmation could not be read',
    detail:
      'Your action went through; only the read that shows the result did not answer. Re-open the ' +
      'record to see it. Do not repeat the action.',
  },
};

const MOVE_TO_POM_CODES = {
  NON_ROUTABLE_RELAY_FAX_REFERENCE_IN_USE: {
    severity: 'warning',
    outcome: OUTCOME.NOTHING_HAPPENED,
    title: 'Another order already holds this reference',
    detail:
      'Nothing was changed and the fax is still parked. This request cannot be moved to POM ' +
      'while that order exists — find it before deciding what to do with this fax.',
  },
  NON_ROUTABLE_RELAY_FAX_MOVE_UNCONFIRMED: {
    severity: 'error',
    outcome: OUTCOME.STUCK,
    title: 'The move to POM could not be confirmed',
    detail:
      'The request is held and no action is available here. Check whether the order reached ' +
      'POM and escalate to engineering. Do not resend the fax.',
  },
};

export const NON_ROUTABLE_ERROR_COPY = {
  ...READ_CODES,
  ...ACTION_CODES,
  ...POLICY_CODES,
  ...ESP_POM_RETRY_CODES,
  ...RELAY_FAX_CODES,
  ...MOVE_TO_POM_CODES,
};

/**
 * The five codes reachable only through `retryNonRoutableRequestRouting`.
 *
 * They are all covered above now that Retry is built. The list is kept as the CHECKED record of
 * that: `nonRoutableRequestErrors.tests.js` asserts every one of them resolves to real copy rather
 * than falling through to UNKNOWN_NON_ROUTABLE_ERROR — which would be safe, because that is
 * non-retryable, but silent about what actually happened to the request.
 *
 * The pair that matters most mirrors the release split: a retry whose rollback did not land leaves
 * the row STUCK, and a retry that got as far as publishing has already acted.
 */
export const NON_ROUTABLE_RETRY_ONLY_CODES = [
  'NON_ROUTABLE_REQUEST_ROUTING_UNAVAILABLE',
  'NON_ROUTABLE_REQUEST_RETRY_PUBLISH_FAILED',
  'NON_ROUTABLE_REQUEST_RETRY_ROLLBACK_FAILED',
  'NON_ROUTABLE_REQUEST_RETRY_QUEUE_UNAVAILABLE',
  'NON_ROUTABLE_REQUEST_RETRY_READ_BACK_FAILED',
  // POM-6279 P4 — the synchronous ESP / POM Retry.
  'NON_ROUTABLE_REQUEST_RETRY_REPLAY_FAILED',
  'NON_ROUTABLE_REQUEST_RETRY_ROUTED_WITH_ERRORS',
  'NON_ROUTABLE_REQUEST_RETRY_REPARKED_WITH_ERRORS',
  'NON_ROUTABLE_REQUEST_RETRY_OUTCOME_UNKNOWN',
  'NON_ROUTABLE_REQUEST_RETRY_POM_ORDER_EXISTS',
  'NON_ROUTABLE_REQUEST_RETRY_RULE_LINES_UNPARKED',
];

/** POM-6279 — the RELAY_FAX_* codes raised by the actions on a parked relay fax. */
export const RELAY_FAX_ACTION_CODES = Object.keys(RELAY_FAX_CODES);

// Anything we do not recognise. Deliberately pessimistic about the retry: an unknown code on a
// screen whose buttons dispense medication is not an invitation to click again.
export const UNKNOWN_NON_ROUTABLE_ERROR = {
  severity: 'error',
  outcome: OUTCOME.UNKNOWN,
  title: 'That did not complete',
  detail:
    'The result is not something this screen recognises, so it cannot say what happened to the ' +
    'request. Refresh the queue and check the record before acting again.',
};

/**
 * POM-6279 review L7 — ROUTING_UNAVAILABLE says WHY in `retryRefusal`, and each reason sends the
 * operator somewhere different. Keyed by that refusal; any other value keeps the generic copy.
 * The refusals are RuleRetryRefusal (nonRoutableRetryService.ts, intake) and PomOrderReplayRefusal
 * (pomOrderRuleRouting/types.ts, ESP / POM). All are raised BEFORE any write, with the row back at
 * Pending.
 */
const ROUTING_UNAVAILABLE_BY_REFUSAL = {
  RULES_UNAVAILABLE: {
    title: 'The routing rules are unavailable right now',
    detail:
      'Nothing was replayed and the request is still parked: replaying it without its routing ' +
      'rule would fill it locally. Try again once the routing rules are available.',
  },
  RULE_NO_LONGER_ROUTES: {
    title: 'No routing rule routes this order any more',
    detail:
      'Nothing was replayed and the request is still parked: replaying it would fill it locally, ' +
      'bypassing the rule that parked it. Fix or restore the routing rule and try again, or cancel ' +
      'it.',
  },
  RELAY_LINES_NOW_SPLIT: {
    title: 'Some parked lines no longer match a routing rule',
    detail:
      'Nothing was replayed and the lines are still parked: they would now be filled locally ' +
      'beside an order that already exists. Fix the routing rule and try again, or cancel them.',
  },
  // A contract break between the hub and Core — fixed by engineering, so clicking again fails
  // identically. The only refusal that is not retryable.
  RULE_ROUTING_UNREADABLE: {
    retryable: false,
    title: 'The routing answer could not be read',
    detail:
      'Nothing was replayed and the request is still parked. This needs an engineering fix — ' +
      'escalate; trying again will fail the same way.',
  },
};

const REFERENCE_IN_USE_BY_CONFLICT = {
  OTHER_RELAY_ORDER: {
    title: 'Another relay order already holds this reference',
    detail:
      'Nothing was changed and the fax is still parked. It cannot be moved to POM while that ' +
      'relay order exists.',
  },
  RX_REQUEST_EXISTS: {
    title: 'A POM order already exists for this reference',
    detail:
      'Nothing was changed and the fax is still parked. Moving it would fill it a second time — ' +
      'check the existing POM order before deciding what to do with this fax.',
  },
  LINES_DIFFER: {
    title: "The saved order no longer matches this fax's lines",
    detail:
      'Nothing was changed and the fax is still parked. It cannot be moved to POM — escalate to ' +
      'engineering.',
  },
};

const COPY_BY_DETAIL_CODE = {
  NON_ROUTABLE_REQUEST_ROUTING_UNAVAILABLE: ROUTING_UNAVAILABLE_BY_REFUSAL,
  NON_ROUTABLE_RELAY_FAX_REFERENCE_IN_USE: REFERENCE_IN_USE_BY_CONFLICT,
};

/**
 * L8 — READ_FAILED raised by an ACTION's pre-flight read. The queue-read copy ("this queue did not
 * load") describes something that did not happen: the queue loaded, the one request did not.
 */
const ACTION_READ_FAILED = {
  severity: 'error',
  outcome: OUTCOME.NOTHING_HAPPENED,
  title: 'The request could not be read',
  detail: 'Nothing was changed. Refresh the queue and try again.',
};

/** graphql-request puts the typed code at response.errors[0].extensions.code. */
export const nonRoutableErrorCode = (error) => error?.response?.errors?.[0]?.extensions?.code;

/**
 * The secondary CODE an action attaches — a Retry's `retryRefusal` (why the replay was refused,
 * e.g. RULE_NO_LONGER_ROUTES) or `replayErrorCode` (what the replay failed with), or Move to POM's
 * `conflict`. Codes only, never a message, so it is safe to show beside the primary code for an
 * operator to quote to engineering.
 */
export const nonRoutableErrorDetailCode = (error) => {
  const extensions = error?.response?.errors?.[0]?.extensions;
  return extensions?.retryRefusal ?? extensions?.replayErrorCode ?? extensions?.conflict;
};

/**
 * Describe a typed code. Always returns an entry — an unrecognised code must never fall through
 * to silence, and an unrecognised code is treated pessimistically.
 */
export const describeNonRoutableErrorCode = (code) => {
  const copy = NON_ROUTABLE_ERROR_COPY[code];
  return { code, ...(copy ?? UNKNOWN_NON_ROUTABLE_ERROR) };
};

/** Same, straight from a graphql-request error. */
export const describeNonRoutableError = (error) => {
  const described = describeNonRoutableErrorCode(nonRoutableErrorCode(error));
  const extensions = error?.response?.errors?.[0]?.extensions;
  const detailCode = nonRoutableErrorDetailCode(error);
  const refined = COPY_BY_DETAIL_CODE[described.code]?.[detailCode];

  return {
    ...described,
    ...refined,
    ...(detailCode ? { detailCode } : {}),
    // M4 — Cancel fax's read-back failure says whether the fax may already be at the pharmacy.
    // Carried only when the server sent a boolean; the dialog treats absence as "it may".
    ...(typeof extensions?.faxMayHaveBeenSent === 'boolean'
      ? { faxMayHaveBeenSent: extensions.faxMayHaveBeenSent }
      : {}),
  };
};

/** Same, for an error an ACTION raised (the dialogs) — see ACTION_READ_FAILED. */
export const describeNonRoutableActionError = (error) => {
  const described = describeNonRoutableError(error);
  return described.code === 'NON_ROUTABLE_REQUEST_READ_FAILED'
    ? { ...described, ...ACTION_READ_FAILED }
    : described;
};

/**
 * True when the irreversible action actually happened despite the error — i.e. an order exists, or
 * the request is permanently cancelled.
 *
 * It is what separates "tell the operator this is done" from "tell them nothing happened"; it does
 * NOT close the dialog and does not refetch. The dialog stays open so the message is read, the
 * confirm button is withheld by `nonRoutableRetryIsSafe`, and the list refetch is dispatched by
 * the thunk itself on every outcome.
 */
export const nonRoutableActionSucceeded = (described) => described?.outcome === OUTCOME.DONE;

/**
 * Whether to leave the confirm button on screen for another attempt.
 *
 * It is an EXPLICIT per-code flag rather than `outcome === NOTHING_HAPPENED`, because most of the
 * "nothing happened" codes still make a second click pointless — the row is no longer Pending, or
 * it is a build problem, or someone else is mid-resolution — and a button that can only fail
 * again is an invitation to keep clicking at a screen that dispenses medication. The retryable
 * codes are named in nonRoutableRequestErrors.tests.js: the publishes that failed and rolled the
 * row back to PENDING, the routing refusal, and the ESP / POM replay failure the backend resumes.
 */
export const nonRoutableRetryIsSafe = (described) => described?.retryable === true;
